import { useId, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GitMerge, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { mergeAuthUserTelegram, type AuthUser } from '@/lib/api'

interface MergeTelegramUserDialogProps {
  source: AuthUser
  users: AuthUser[]
  onMerged: (target: AuthUser) => void
}

export function MergeTelegramUserDialog({ source, users, onMerged }: MergeTelegramUserDialogProps) {
  const [open, setOpen] = useState(false)
  const [targetUsername, setTargetUsername] = useState('')
  const targetSelectId = useId()
  const queryClient = useQueryClient()
  const targets = users.filter((user) => user.active && user.username !== source.username)
  const merge = useMutation({
    mutationFn: () => mergeAuthUserTelegram(source.username, targetUsername),
    onSuccess: async (target) => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
      setOpen(false)
      onMerged(target)
    },
  })

  if (source.has_password !== false || source.role !== 'user'
    || !source.telegram_accounts?.length || Object.values(source.permissions || {}).some(Boolean)) {
    return null
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Merge ${source.username} into another user`}
        onClick={() => {
          setTargetUsername('')
          merge.reset()
          setOpen(true)
        }}
      >
        <GitMerge className="mr-2 h-4 w-4" />
        Merge
      </Button>
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!merge.isPending) setOpen(nextOpen) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Telegram user</DialogTitle>
            <DialogDescription>
              Move Telegram identities and saved settings from “{source.username}” into an existing account.
              The destination keeps its password, permissions, and existing settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Existing position history is preserved. “{source.username}” will be removed and its sessions signed out.
              Future Telegram sign-ins will use the destination account.
            </p>
            <div className="space-y-2">
              <Label htmlFor={targetSelectId}>Destination account</Label>
              <Select value={targetUsername} onValueChange={setTargetUsername} disabled={merge.isPending}>
                <SelectTrigger id={targetSelectId}>
                  <SelectValue placeholder="Choose an existing account" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((user) => (
                    <SelectItem key={user.username} value={user.username}>{user.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!targets.length && <p className="text-sm text-muted-foreground">No other active accounts are available.</p>}
            </div>
            {merge.error && <p role="alert" className="text-sm text-destructive">{merge.error.message}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={merge.isPending} onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              disabled={merge.isPending || !targets.some((user) => user.username === targetUsername)}
              onClick={() => merge.mutate()}
            >
              {merge.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Merge into account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
